/**
 * Erwähnungen mit `@Name` — die Textarbeit, ohne React.
 *
 * Der Text bleibt gewöhnlicher Text: Gespeichert wird `@Tobias Döhring`, nicht
 * `@[Tobias Döhring](uuid)`. Dieselbe Beschreibung steht später in einer Mail,
 * im Verlauf und in `scripts/tickets.py show` — eine Auszeichnung, die nur diese
 * Oberfläche versteht, wäre dort Müll. Wen es betrifft, schickt der Browser
 * **daneben** als Liste von Konto-IDs mit.
 *
 * Deshalb wird die Liste bei jeder Änderung **aus dem Text neu abgeleitet** und
 * nicht beim Einfügen gesammelt: Wer einen Namen wieder herauslöscht, meint ihn
 * auch nicht mehr. Eine mitgeführte Liste würde dabei still falsch.
 */

export interface ErwaehnbarePerson {
  accountId: string;
  name: string;
}

/** Was der Browser gerade tippt: `@` plus das, was danach schon dasteht. */
export interface AngefangeneErwaehnung {
  /** Position des `@` im Text. */
  von: number;
  /** Position hinter dem bereits getippten Namen. */
  bis: number;
  /** Was nach dem `@` steht — danach wird gefiltert. */
  suche: string;
}

const WORTZEICHEN = /[\p{L}\p{N}_]/u;

function istWortzeichen(zeichen: string | undefined): boolean {
  return !!zeichen && WORTZEICHEN.test(zeichen);
}

/**
 * Die Erwähnung, in der der Schreibcursor gerade steht — oder `null`.
 *
 * Gesucht wird vom Cursor nach links bis zum `@`. Bricht vorher ein Zeilenumbruch
 * oder ein zweites `@`, ist es keine; und steht vor dem `@` ein Wortzeichen,
 * ist es eine Mailadresse und keine Erwähnung.
 */
export function angefangeneErwaehnung(
  text: string,
  cursor: number
): AngefangeneErwaehnung | null {
  for (let stelle = cursor - 1; stelle >= 0; stelle -= 1) {
    const zeichen = text[stelle];
    if (zeichen === '@') {
      if (istWortzeichen(text[stelle - 1])) return null;
      const suche = text.slice(stelle + 1, cursor);
      // Zwei Wörter reichen für „Vorname Nachname"; alles darüber ist Fließtext,
      // in dem ein offenes `@` weit oben steht.
      if (suche.split(' ').length > 2) return null;
      return { von: stelle, bis: cursor, suche };
    }
    if (zeichen === '\n' || zeichen === undefined) return null;
    // Ein Leerzeichen beendet die Suche nicht sofort — „@Tobias Dö" soll noch
    // filtern. Zwei hintereinander sind aber Fließtext.
    if (zeichen === ' ' && text[stelle - 1] === ' ') return null;
  }
  return null;
}

/** Nach der Eingabe filtern: Teiltreffer in Vor- oder Nachnamen, höchstens acht. */
export function passendePersonen(
  personen: ErwaehnbarePerson[],
  suche: string,
  maximal = 8
): ErwaehnbarePerson[] {
  const gesucht = suche.trim().toLowerCase();
  if (!gesucht) return personen.slice(0, maximal);
  return personen
    .filter((person) =>
      person.name
        .toLowerCase()
        .split(' ')
        .some((teil) => teil.startsWith(gesucht))
    )
    .slice(0, maximal);
}

/**
 * Den Namen an der angefangenen Stelle einsetzen.
 *
 * Gibt den neuen Text und die Cursorposition danach zurück — ohne das Zweite
 * springt der Cursor ans Ende und der nächste Tastendruck landet falsch.
 */
export function einsetzen(
  text: string,
  stelle: AngefangeneErwaehnung,
  person: ErwaehnbarePerson
): { text: string; cursor: number } {
  const davor = text.slice(0, stelle.von);
  const danach = text.slice(stelle.bis);
  // Ein Leerzeichen dahinter, damit weitergeschrieben werden kann — aber nicht
  // vor einem Satzzeichen („@Tobias Döhring , bitte") und nicht doppelt. Am
  // Textende gehört es hin: Dort tippt jemand weiter.
  const trenner = /^[\s.,;:!?)\]}]/.test(danach) ? '' : ' ';
  const eingesetzt = `@${person.name}${trenner}`;
  return { text: davor + eingesetzt + danach, cursor: stelle.von + eingesetzt.length };
}

const ANFANG_ERWAEHNUNG = /(?<![\p{L}\p{N}_])@/gu;

/**
 * Wen der Text erwähnt — abgeleitet, nicht mitgeführt.
 *
 * Verglichen wird gegen den vollen Namen und, als Rückfall, gegen den Vornamen:
 * Die Auswahl setzt den vollen Namen ein, aber wer ihn auf `@Tobias` kürzt,
 * meint weiterhin dieselbe Person — und der Server sieht es genauso
 * (`testing_ticket_mentions._steht_im_text`).
 *
 * Bei zwei Personen mit demselben Vornamen zählt nur der volle Name. Lieber
 * niemand benachrichtigt als der Falsche.
 */
export function erwaehnteKonten(text: string, personen: ErwaehnbarePerson[]): string[] {
  if (!text) return [];
  const klein = text.toLowerCase();
  const stellen = [...klein.matchAll(ANFANG_ERWAEHNUNG)].map((treffer) => treffer.index ?? 0);
  if (stellen.length === 0) return [];

  const vornamen = new Map<string, number>();
  for (const person of personen) {
    const vorname = person.name.trim().split(' ')[0]?.toLowerCase() ?? '';
    if (vorname) vornamen.set(vorname, (vornamen.get(vorname) ?? 0) + 1);
  }

  const gefunden: string[] = [];
  for (const person of personen) {
    const voll = person.name.trim().toLowerCase();
    const vorname = voll.split(' ')[0] ?? '';
    const eindeutig = (vornamen.get(vorname) ?? 0) === 1;
    const passt = stellen.some((stelle) => {
      const rest = klein.slice(stelle + 1);
      for (const kandidat of eindeutig ? [voll, vorname] : [voll]) {
        if (!kandidat || !rest.startsWith(kandidat)) continue;
        if (!istWortzeichen(rest[kandidat.length])) return true;
      }
      return false;
    });
    if (passt && !gefunden.includes(person.accountId)) gefunden.push(person.accountId);
  }
  return gefunden;
}

/** Ein Stück Text, wie die Anzeige es braucht. */
export interface TextStueck {
  text: string;
  /** `@Name` einer bekannten Person. */
  erwaehnt: boolean;
  /** Nummer eines Tickets, das es wirklich gibt — sonst `undefined`. */
  ticket?: number;
}

/**
 * Den Text in Stücke zerlegen, damit die Anzeige Namen und Ticketnummern
 * hervorheben kann.
 *
 * **Ein** Durchgang für beides, nicht zwei nacheinander: Zwei Zerleger müssten
 * ihre Ergebnisse wieder ineinanderschieben, und die Grenzen der Stücke sind
 * genau die Stelle, an der das schiefgeht.
 *
 * Hervorgehoben wird nur, was zu einer bekannten Person und einem vorhandenen
 * Ticket gehört. Sonst sähe jede Mailadresse wie eine Erwähnung und jede Zahl
 * mit Bindestrich wie ein Ticket aus — und ein Link ins Leere ist schlechter
 * als gewöhnlicher Text.
 */
export function zerlegen(
  text: string,
  personen: ErwaehnbarePerson[],
  verweise?: { muster: RegExp; vorhanden: (nummer: number) => boolean }
): TextStueck[] {
  if (!text) return [];
  // Dieselbe Regel wie in `erwaehnteKonten`: voller Name, und der Vorname
  // allein, wenn er eindeutig ist. Sonst schickte „@Tobias" eine Mail, wäre im
  // Text aber nicht hervorgehoben — und niemand sähe, dass es gewirkt hat.
  const namen = erwaehnbareSchreibweisen(personen);
  if (namen.length === 0 && !verweise) return [{ text, erwaehnt: false }];

  // Die Fundstellen der Ticketnummern vorab: Ein globales RegExp mitten in einer
  // Schleife über denselben Text hätte einen wandernden `lastIndex`, und der ist
  // die klassische Quelle für übersprungene Treffer.
  const nummern = new Map<number, number>();
  if (verweise) {
    for (const treffer of text.matchAll(verweises(verweise.muster))) {
      const nummer = Number(treffer[1]);
      if (treffer.index !== undefined && verweise.vorhanden(nummer)) {
        nummern.set(treffer.index, treffer[0].length);
      }
    }
  }

  const stuecke: TextStueck[] = [];
  let rest = 0;
  let stelle = 0;
  const vorherSchneiden = () => {
    if (stelle > rest) stuecke.push({ text: text.slice(rest, stelle), erwaehnt: false });
  };

  while (stelle < text.length) {
    const laenge = nummern.get(stelle);
    if (laenge !== undefined) {
      vorherSchneiden();
      const rohtext = text.slice(stelle, stelle + laenge);
      const nummer = Number(rohtext.replace(/\D/g, ''));
      stuecke.push({ text: rohtext, erwaehnt: false, ticket: nummer });
      stelle += laenge;
      rest = stelle;
      continue;
    }
    if (text[stelle] === '@' && !istWortzeichen(text[stelle - 1])) {
      const danach = text.slice(stelle + 1).toLowerCase();
      const treffer = namen.find((name) => {
        const klein = name.toLowerCase();
        return danach.startsWith(klein) && !istWortzeichen(text[stelle + 1 + name.length]);
      });
      if (treffer) {
        vorherSchneiden();
        stuecke.push({ text: `@${treffer}`, erwaehnt: true });
        stelle += treffer.length + 1;
        rest = stelle;
        continue;
      }
    }
    stelle += 1;
  }
  stelle = text.length;
  vorherSchneiden();
  return stuecke;
}

/**
 * Alle Schreibweisen, unter denen eine Person im Text stehen kann: der volle
 * Name und, bei eindeutigem Vornamen, auch dieser allein. Längste zuerst, damit
 * „@Tobias Döhring" nicht als „@Tobias" plus Resttext zerfällt.
 */
function erwaehnbareSchreibweisen(personen: ErwaehnbarePerson[]): string[] {
  const vornamen = new Map<string, number>();
  for (const person of personen) {
    const vorname = person.name.trim().split(' ')[0]?.toLowerCase() ?? '';
    if (vorname) vornamen.set(vorname, (vornamen.get(vorname) ?? 0) + 1);
  }
  const schreibweisen = new Set<string>();
  for (const person of personen) {
    const voll = person.name.trim();
    if (!voll) continue;
    schreibweisen.add(voll);
    const vorname = voll.split(' ')[0] ?? '';
    if (vorname && vorname !== voll && (vornamen.get(vorname.toLowerCase()) ?? 0) === 1) {
      schreibweisen.add(vorname);
    }
  }
  return [...schreibweisen].sort((a, b) => b.length - a.length);
}

/** Eine eigene Kopie des Musters je Durchgang — `lastIndex` ist Zustand. */
function verweises(muster: RegExp): RegExp {
  return new RegExp(muster.source, muster.flags.includes('g') ? muster.flags : `${muster.flags}g`);
}
